import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Container,
  Avatar,
  Button,
  Box,
  useMediaQuery,
  Badge,
  Popover,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import Logo from "../../assets/logo.jpg";
import dayjs from "dayjs";
import {
  clearUserNotifications,
  deleteUserNotification,
  getUnreadMessageCount,
  markUserNotificationsRead,
} from "../../utils/userNotifications";

const Appbar = ({
  handleDrawerOpen,
  userDetails,
  setUserDetails,
  isMobile = true,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const messages = userDetails?.messages || [];
  const unreadCount = getUnreadMessageCount(messages);

  const handleLogout = async () => {
    localStorage.removeItem("care_token");
    navigate("/login");
  };

  const handleNotificationsClick = async (event) => {
    setAnchorEl(event.currentTarget);

    const hasUnread = messages.some((message) => !message.read);
    if (!hasUnread) {
      return;
    }

    try {
      const token = localStorage.getItem("care_token");
      if (!token) {
        return;
      }

      await markUserNotificationsRead(token);
      if (setUserDetails) {
        setUserDetails((prev) => ({
          ...prev,
          messages: (prev?.messages || []).map((message) => ({
            ...message,
            read: true,
          })),
        }));
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const handleCloseNotifications = () => {
    setAnchorEl(null);
  };

  const handleDeleteNotification = async (notification) => {
    try {
      const token = localStorage.getItem("care_token");
      if (!token) {
        return;
      }

      await deleteUserNotification(token, notification);
      if (setUserDetails) {
        setUserDetails((prev) => ({
          ...prev,
          messages: (prev?.messages || []).filter((message) => {
            if (notification._id && message._id) {
              return message._id !== notification._id;
            }

            return !(
              message.message === notification.message &&
              new Date(message.receivedAt).getTime() ===
                new Date(notification.receivedAt).getTime()
            );
          }),
        }));
      }
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  const handleClearNotifications = async () => {
    try {
      const token = localStorage.getItem("care_token");
      if (!token) {
        return;
      }

      await clearUserNotifications(token);
      if (setUserDetails) {
        setUserDetails((prev) => ({
          ...prev,
          messages: [],
        }));
      }
    } catch (error) {
      console.error("Error clearing notifications:", error);
    }
  };

  const formatDate = (date) => dayjs(date).format("DD-MM-YYYY");

  const open = Boolean(anchorEl);
  const id = open ? "notifications-popover" : undefined;

  return (
    <AppBar position="fixed" sx={{ bgcolor: "#00C261", zIndex: 1100 }}>
      <Container>
        <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerOpen}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Avatar alt="Company Logo" src={Logo} sx={{ marginRight: 2 }} />
            {!isSmallScreen && (
              <Typography variant="h6" component="div">
                Bevogtet Dashboard
              </Typography>
            )}
          </Box>
          <Box
            className="notification-container"
            sx={{ display: "flex", alignItems: "center" }}
          >
            {!isSmallScreen && userDetails && (
              <Typography variant="subtitle1" sx={{ marginRight: 2 }}>
                Welcome, {userDetails.fName}
              </Typography>
            )}
            {userDetails && (
              <IconButton
                color="inherit"
                aria-label="notifications"
                onClick={handleNotificationsClick}
              >
                <Badge badgeContent={unreadCount} color="secondary">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}
            <Button color="inherit" onClick={handleLogout}>
              Logout
            </Button>
          </Box>
        </Toolbar>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleCloseNotifications}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Box sx={{ p: 2, minWidth: 320, maxWidth: 360 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle1">Notifications</Typography>
              {messages.length > 0 && (
                <Button
                  size="small"
                  color="error"
                  onClick={handleClearNotifications}
                >
                  Clear all
                </Button>
              )}
            </Box>
            {messages.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No notifications
              </Typography>
            ) : (
              <List sx={{ pt: 0 }}>
                {messages.map((message, index) => (
                  <ListItem
                    key={message._id || `${message.receivedAt}-${index}`}
                    disablePadding
                    sx={{
                      alignItems: "flex-start",
                      py: 1,
                      borderBottom: "1px solid #eee",
                      bgcolor: message.read ? "transparent" : "rgba(0, 194, 97, 0.08)",
                    }}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete notification"
                        onClick={() => handleDeleteNotification(message)}
                        size="small"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={message.message}
                      secondary={`Admin • ${formatDate(message.receivedAt)}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Popover>
      </Container>
    </AppBar>
  );
};

export default Appbar;
